import React from 'react';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import useScrollReveal from '../useScrollReveal';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';


export default function FeatureCardsNumberedSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'grid';
    const items = content.items || [];
    const cols = config.columns || 3;

    if (items.length === 0) return null;

    const colsClass = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3';

    const NumberBadge = ({ num }) => (
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 text-white"
            style={{ backgroundColor: primaryColor }}>
            {num}
        </div>
    );

    const FeatureCard = ({ item, idx }) => {
        const num = item.number || idx + 1;
        const accentWord = item.accent_word || '';
        const title = item.title || '';
        const titleParts = accentWord ? title.split(accentWord) : [title];

        if (variant === 'minimal') {
            return (
                <div className={`flex items-start gap-4 p-4 ${anim}`}>
                    <NumberBadge num={num} />
                    <div className="min-w-0">
                        <h3 className={`text-lg font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>
                            {accentWord ? <>{titleParts[0]}<span style={{ color: primaryColor }}>{accentWord}</span>{titleParts[1] || ''}</> : title}
                        </h3>
                        {item.description && <p className={`text-sm mt-1 ${s.dark ? 'text-white/60' : 'text-gray-500'}`}>{item.description}</p>}
                    </div>
                </div>
            );
        }

        return (
            <div className={`rounded-2xl overflow-hidden transition-all hover:shadow-xl group ${s.dark ? 'bg-white/5 border border-white/10 hover:bg-white/10' : 'bg-white border border-gray-100 shadow-lg hover:shadow-2xl'} ${anim}`}>
                {item.image && (
                    <div className="aspect-[16/10] overflow-hidden">
                        <img src={item.image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                )}
                <div className={`p-5 sm:p-6 ${!item.image ? 'min-h-[12rem] flex flex-col' : ''}`}>
                    <div className="flex items-start gap-3 mb-3">
                        <NumberBadge num={num} />
                        <h3 className={`text-lg font-bold leading-tight ${s.dark ? 'text-white' : 'text-gray-900'}`}>
                            {accentWord ? <>{titleParts[0]}<span style={{ color: primaryColor }}>{accentWord}</span>{titleParts[1] || ''}</> : title}
                        </h3>
                    </div>
                    {item.description && <p className={`text-sm flex-1 ${s.dark ? 'text-white/60' : 'text-gray-500'}`}>{item.description}</p>}
                    {item.link && item.link_text && (
                        <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-semibold mt-3 hover:opacity-80 transition-opacity" style={{ color: primaryColor }}>
                            {item.link_text} <span>→</span>
                        </a>
                    )}
                </div>
            </div>
        );
    };

    // Timeline variant
    if (variant === 'timeline') {
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />

                <div ref={ref} className={`relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 relative">
                        <div className={`absolute left-[18px] top-0 bottom-0 w-0.5 ${s.dark ? 'bg-white/10' : 'bg-gray-200'}`} />
                        <div className="space-y-6">
                            {items.map((item, i) => (
                                <div key={i} className="flex items-start gap-5 relative">
                                    <div className="relative z-10"><NumberBadge num={item.number || i + 1} /></div>
                                    <div className={`flex-1 rounded-xl p-4 ${s.dark ? 'bg-white/5 border border-white/10' : 'bg-white shadow-md border border-gray-100'}`}>
                                        <h3 className={`font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                                        {item.description && <p className={`text-sm mt-1 ${s.dark ? 'text-white/60' : 'text-gray-500'}`}>{item.description}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    // Default: grid
    return (
        <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} position="top" color={s.accent} />
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`mt-8 grid ${colsClass} gap-4 sm:gap-6`}>
                    {items.map((item, i) => <FeatureCard key={i} item={item} idx={i} />)}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
        </section>
    );
}
